//@flow
import * as Immutable from 'immutable';

import { HistoricalData } from 'common/models/flow/HistoricalData';
import type { HistoricalDataModelType } from 'common/models/flow/HistoricalData';

import { MasterRider } from './MasterRider';
import { RiderId } from './RiderId';

import {
    validatable,
    validate,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';
import DataUtils from 'common/utils/DataUtils';
import NumberUtils from 'common/utils/NumberUtils';

import I18nHelper from 'common/helpers/I18nHelper';

import { Revenue } from './Revenue';

import { TourDay } from './TourDay';
import RegExUtil from 'common/utils/RegExUtil';

const translationsNamespace = ['validations'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

@validatable
class InternalHistoricalData extends HistoricalData {
    static fromJS(json: HistoricalDataModelType): InternalHistoricalData {
        const state: Object = Object.assign({}, json);
        state.riderId = state.riderId
            ? RiderId.fromJS(state.riderId)
            : state.riderId;
        state.rider = state.rider
            ? MasterRider.fromJS(state.rider)
            : state.rider;
        state.recentRevenue = state.recentRevenue
            ? Revenue.fromJS(state.recentRevenue)
            : state.recentRevenue;
        state.currentPjpDayInfo = TourDay.fromJS(state.currentPjpDayInfo);
        return new this(Immutable.Map(state));
    }

    get riderId(): ?RiderId {
        return this._state.get('riderId');
    }

    get rider(): ?MasterRider {
        return this._state.get('rider');
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        const { recentRevenue } = this;
        if (recentRevenue && recentRevenue.hasValidationErrors()) {
            return true;
        }
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        let { recentRevenue } = this;
        if (recentRevenue) {
            recentRevenue = recentRevenue.setAllValidationErrors();
        }
        return this.setRecentRevenue(recentRevenue);
    }

    @validate
    setCurrentBeatId(currentBeatId: string): this {
        return super.setCurrentBeatId(currentBeatId);
    }

    validateCurrentBeatId() {
        // Since trim internaly uses regex, using it is fine
        const notAllowedRegEx = new RegExp(
            RegExUtil.alphaNumericWithUnderscoreAndDash
        );

        if (!notAllowedRegEx.test(this.currentBeatId)) {
            return nsTranslate(
                'outletValidationErrors.historicalData.currentPjpNameInvalid'
            );
        }
    }

    @validate
    setRecentRevenue(revenue: ?Revenue): this {
        return super.setRecentRevenue(revenue);
    }

    validateRecentRevenue() {
        return (
            this.recentRevenue &&
            this.recentRevenue.getValidationMessage('amount')
        );
    }

    @validate
    setAverageSkusSold(averageSkusSold: number): this {
        return super.setAverageSkusSold(averageSkusSold);
    }

    validateAverageSkusSold() {
        if (
            !DataUtils.isEmpty(this.averageSkusSold) &&
            !NumberUtils.isInteger(this.averageSkusSold)
        ) {
            return nsTranslate(
                'outletValidationErrors.historicalData.averageSkusSoldNotAnInteger'
            );
        }
    }

    get recentRevenue(): ?Revenue {
        return this._state.get('recentRevenue');
    }

    // Alternative to lazy loading of nested models (init and cleaning)
    defaultSanitize(): ?this {
        const sanitizedHistoricalData = super.defaultSanitize();

        if (sanitizedHistoricalData) {
            const sanitizedRiderId = sanitizedHistoricalData.riderId?.defaultSanitize();
            const sanitizedRider = sanitizedHistoricalData.rider?.defaultSanitize();
            const sanitizedRecentRevenue = sanitizedHistoricalData.recentRevenue?.defaultSanitize();

            return sanitizedHistoricalData
                .setRiderId(sanitizedRiderId)
                .setRider(sanitizedRider)
                .setRecentRevenue(sanitizedRecentRevenue);
        }
        return sanitizedHistoricalData;
    }

    static defaultInit({ props }): InternalHistoricalData {
        const riderId = RiderId.defaultInit({ props });
        const rider = MasterRider.defaultInit({ props });
        const recentRevenue = Revenue.defaultInit({ props });

        return InternalHistoricalData.fromJS({})
            .setRiderId(riderId)
            .setRider(rider)
            .setRecentRevenue(recentRevenue);
    }

    get frozenRiderId(): ?RiderId {
        return this._state.get('frozenRiderId');
    }

    get currentPjpDayInfo(): TourDay {
        return this._state.get('currentPjpDayInfo');
    }
}

export { InternalHistoricalData as HistoricalData };
